import { useCallback, useMemo, useState } from 'react';
import { Dialog, DialogProps } from './Dialog';
import { IPropertyGridProps, PropertyGrid } from 'components/property-editors/PropertyGrid';
import {
  editComplete,
  getInvalidReasons,
} from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { EditCompleteType } from '@decisions/server/DecisionsFramework/Design/Properties';
import { IActionsProps } from './ButtonPanel';
import { AlertDialog } from './AlertDialog';
import { BreakLevel } from '@decisions/server/DecisionsFramework';
import { SkeletonLoader } from 'loaders/SkeletonLoader';
export interface PropertyGridDialogProps {
  dialogProps: DialogProps;
  propertyGridProps: IPropertyGridProps;
  /**Action name, default is 'Ok' */
  actionName?: string;
  /**Edit complete with saved */
  onSubmit?: () => void;
  /**Canceled using title cross action */
  onCancel?: () => void;
  /**Do not call edit complete on ok */
  preserveContextId?: boolean;
  /**To show spinner inside the dialog, useful when context id is retrived from server*/
  isLoading?: boolean;
}
export function PropertyGridDialog(props: PropertyGridDialogProps) {
  const [breakingIssues, setBreakingIssues] = useState({
    show: false,
    issues: [] as string[],
    title: '',
  });
  const currentEditingContextId = props.propertyGridProps.snapshot
    ? props.propertyGridProps.snapshot.EditingContextId
    : props.propertyGridProps.editingContextId;

  const validate = useCallback(async () => {
    let isValid = true;
    const allValidations = await getInvalidReasons({ contextProviderRegistrationId: currentEditingContextId });
    if (allValidations && allValidations.length) {
      const fatalValidations = allValidations.filter(v => v.Level === BreakLevel.Fatal);
      if (fatalValidations.length) {
        isValid = false;
        setBreakingIssues({
          issues: fatalValidations.map(v => v.Reason),
          show: true,
          title: `${fatalValidations.length} validation break(s)`,
        });
      }
    }
    return isValid;
  }, [currentEditingContextId]);

  const handleOk = useCallback(async () => {
    const isValid = await validate();
    if (!isValid) return;
    if (!props.preserveContextId)
      await editComplete({
        completeType: EditCompleteType.Saved,
        contextProviderRegistrationId: currentEditingContextId,
      });
    props.onSubmit && props.onSubmit();
    props.dialogProps.onClose && props.dialogProps.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dialogProps.onClose, props.propertyGridProps, props.onSubmit]);

  const dialogActions: IActionsProps[] = useMemo(() => {
    return [
      ...(props.dialogProps.dialogActions?.actions || []),
      { name: props.actionName || 'Ok', onClick: handleOk } as IActionsProps,
    ];
  }, [handleOk, props.actionName, props.dialogProps.dialogActions]);

  const handleClose = useCallback(async () => {
    await editComplete({
      completeType: EditCompleteType.Canceled,
      contextProviderRegistrationId: currentEditingContextId,
    });
    props.onCancel && props.onCancel();
    props.dialogProps.onClose && props.dialogProps.onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dialogProps.onClose, currentEditingContextId, props.onCancel]);

  const handleBreakingIssuesClose = useCallback(() => setBreakingIssues({ issues: [], show: false, title: '' }), []);

  const propertyGrid = useMemo(() => {
    if (props.isLoading) {
      return <SkeletonLoader height={150} />;
    }
    if (!props.dialogProps.open || (!currentEditingContextId && !props.propertyGridProps.snapshot)) return null;
    return (
      <PropertyGrid
        {...props.propertyGridProps}
        snapshot={props.propertyGridProps.snapshot}
        editingContextId={currentEditingContextId}
      />
    );
  }, [currentEditingContextId, props.dialogProps.open, props.isLoading, props.propertyGridProps]);

  return (
    <>
      <Dialog
        {...props.dialogProps}
        onClose={handleClose}
        dialogActions={{ actions: dialogActions }}
        keyboard={{ autoFocusFirst: true, trapTabbing: true }}
      >
        {propertyGrid}
      </Dialog>
      <AlertDialog
        open={breakingIssues.show}
        messages={breakingIssues.issues}
        onClose={handleBreakingIssuesClose}
        title={breakingIssues.title}
        actionName="ok"
        type="Information" //This should be Error, making it info just to maintain legacy, not sure though
      />
    </>
  );
}
